import classNames from 'classnames'
import React, { useCallback, useMemo } from 'react'

import { useSidebar } from '@context/SidebarContext'

import { useAccountSwitch } from '@shared/components/Navigation/ProductSwitch/useAccountSwitch'
import { useProductSwitch } from '@shared/components/Navigation/ProductSwitch/useProductSwitch'
import { useAccountSwitchMenu } from '@shared/components/Navigation/menuSections/useAccountSwitchMenu'
import { useProductSwitchMenu } from '@shared/components/Navigation/menuSections/useProductSwitchMenu'

import { useSidebarMenuSections } from '../hooks/useSidebarMenuSections'
import { SidebarItem } from './SidebarItem'
import { SidebarSubmenu } from './SidebarSubmenu'
import { SidebarSwitcher } from './SidebarSwitcher'
import { SidebarToggle } from './SidebarToggle'

import styles from './Sidebar.module.scss'

/**
 * main sidebar component for setting 3 (LayoutFull).
 *
 * we have fixed position + full height
 * I set width transitions between 96px (collapsed) and 304px (expanded) - to check with figma...
 * sets --sidebar-width CSS variable for content area offset
 *
 * structure: Logo NavItems flex-spacer Switcher Toggle
 */
export const Sidebar: React.FC = () => {
  const { isExpanded, activeSubmenu, closeSubmenu, sidebarWidth, trackPointer } = useSidebar()
  const { sidebarSections } = useSidebarMenuSections()

  const handleMouseLeave = useCallback(() => {
    closeSubmenu()
  }, [closeSubmenu])

  const handleMouseMove = useCallback(
    (event: React.MouseEvent) => {
      trackPointer(event.clientX, event.clientY)
    },
    [trackPointer]
  )

  const handleProductSwitch = useProductSwitch()
  const productSwitchMenu = useProductSwitchMenu(handleProductSwitch)

  const handleAccountSwitch = useAccountSwitch()
  const accountSwitchMenu = useAccountSwitchMenu(handleAccountSwitch)

  const allSections = useMemo(
    () => [...sidebarSections, productSwitchMenu, accountSwitchMenu],
    [sidebarSections, productSwitchMenu, accountSwitchMenu]
  )

  const activeParentItem = useMemo(() => {
    if (!activeSubmenu) return null
    return allSections.find((s) => s.id === activeSubmenu) ?? null
  }, [activeSubmenu, allSections])

  return (
    <>
      <div
        style={{ '--sidebar-width': `${sidebarWidth}px` } as React.CSSProperties}
        className={styles.sidebarZone}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <nav
          className={classNames(styles.sidebar, { [styles.expanded]: isExpanded })}
          role='navigation'
          aria-label='main navigation'
        >
          {/*NAV ITEMS*/}
          <div className={styles.sidebarContent}>
            {sidebarSections.map((section) => (
              <SidebarItem key={section.id} item={section} />
            ))}
          </div>

          {/*BOTTOM AREA : SWITCHER + TOGGLE*/}
          <div className={styles.sidebarBottom}>
            <SidebarSwitcher />
            <SidebarToggle />
          </div>
        </nav>

        {/*SUBMENU PANEL - NEXT TO SIDEBAR*/}
        {activeParentItem && (
          <>
            <div className={styles.submenuBridge} />
            <SidebarSubmenu parentItem={activeParentItem} />
          </>
        )}
      </div>
    </>
  )
}
